import { ChevronDown } from 'lucide-react';
import {
  PAYMENT_CARD_CLASS,
  SUMMARY_SECTION_ID,
  formatEuro,
  type PaymentAmounts,
  type PaymentMethodKind,
} from '../utils/paymentDisplay';
import { SummaryRow } from './PaymentUi';

/** Montant mis en avant : mensualité ou total, sur fond teinté de la couleur d'accent. */
export function AmountHighlight({ amounts, className = '' }: { amounts: PaymentAmounts; className?: string }) {
  const isMonthly = amounts.kind === 'monthly';
  const mainAmount = amounts.kind === 'monthly' ? amounts.monthlyAmount : amounts.total;

  const details: string[] = [];
  if (amounts.kind === 'monthly') {
    if (amounts.installments) details.push(`${amounts.installments} mensualités`);
    if (amounts.total != null) details.push(`total ${formatEuro(amounts.total)}`);
  }

  return (
    <div
      className={`tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-xl tw-bg-[color:var(--pay-accent-soft)] tw-px-4 tw-py-3 ${className}`}
    >
      <div className="tw-min-w-0">
        <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-600">
          {isMonthly ? 'Mensualité' : 'Montant total'}
        </p>
        {details.length > 0 && <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-gray-600">{details.join(' · ')}</p>}
      </div>
      <p className="tw-mb-0 tw-whitespace-nowrap tw-text-2xl tw-font-bold tw-text-[color:var(--pay-ink)]">
        {formatEuro(mainAmount)}
        {isMonthly && <span className="tw-ml-1 tw-text-sm tw-font-medium tw-text-gray-600">/ mois</span>}
      </p>
    </div>
  );
}

/** Détail des montants en bas du récapitulatif. */
export function AmountBreakdown({ amounts, methodKind }: { amounts: PaymentAmounts; methodKind: PaymentMethodKind }) {
  if (amounts.kind === 'monthly') {
    return (
      <div>
        <SummaryRow label="Mensualités">
          {formatEuro(amounts.monthlyAmount)} / mois
          {amounts.installments ? <span className="tw-text-gray-600"> × {amounts.installments} mois</span> : null}
        </SummaryRow>
        {amounts.total != null && (
          <div className="tw-mt-2 tw-border-t tw-border-dashed tw-border-slate-200 tw-pt-2">
            <SummaryRow label="Total" emphasis>
              {formatEuro(amounts.total)}
            </SummaryRow>
          </div>
        )}
        {amounts.serviceFees != null && (
          <p className="tw-mb-0 tw-text-right tw-text-xs tw-text-gray-600">
            dont frais de service : {formatEuro(amounts.serviceFees)}
          </p>
        )}
        <p className="tw-mb-0 tw-mt-3 tw-text-xs tw-text-gray-600">
          Prélèvement automatique mensuel par mandat SEPA, sans engagement au-delà de la période prévue.
        </p>
      </div>
    );
  }

  return (
    <div>
      <SummaryRow label="Total à régler" emphasis>
        {formatEuro(amounts.total)}
      </SummaryRow>
      <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-gray-600">
        {methodKind === 'card' ? 'Paiement unique par carte bancaire.' : 'Paiement unique par prélèvement SEPA.'}
      </p>
    </div>
  );
}

/**
 * Résumé compact affiché en premier sur mobile (le récapitulatif complet est plus bas).
 */
export function CompactAmountSummary({
  title,
  subtitle,
  amounts,
  className = '',
}: {
  title: string;
  subtitle: string | null;
  amounts: PaymentAmounts | null;
  className?: string;
}) {
  return (
    <div className={`${PAYMENT_CARD_CLASS} tw-p-4 ${className}`}>
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <p className="tw-mb-0 tw-truncate tw-text-base tw-font-semibold tw-text-gray-900">{title}</p>
          {subtitle && <p className="tw-mb-0 tw-truncate tw-text-sm tw-text-gray-600">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={() => document.getElementById(SUMMARY_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="tw-inline-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-[color:var(--pay-ink)] hover:tw-underline"
        >
          Détail
          <ChevronDown className="tw-h-4 tw-w-4" aria-hidden="true" />
        </button>
      </div>
      {amounts && <AmountHighlight amounts={amounts} className="tw-mt-3" />}
    </div>
  );
}
